<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
    >
      <a-button type="primary" icon="plus" @click="openModal(null)">
        {{ $t("action.ajouter") }}
      </a-button>
    </div>
    <a-table
      row-key="_id"
      :loading="loading"
      :columns="columns"
      :data-source="rows"
      :pagination="{ pageSize: 10 }"
      :scroll="{ x: 'max-content' }"
    >
      <template slot="cat" slot-scope="text, record">
        {{ record.category && record.category.name }}
      </template>
      <template slot="dir" slot-scope="text">
        {{
          text === "inverse"
            ? $t("comptabilite.directionInverse")
            : $t("comptabilite.directionNormal")
        }}
      </template>
      <template slot="accountingClass" slot-scope="text, record">
        <a-tag v-if="record.accountingClass" color="geekblue">
          {{ record.accountingClass }}
        </a-tag>
        <span v-else-if="parentCategoryClass(record)" class="d-inline-flex align-items-center">
          <span class="text-muted small mr-2">
            {{ $t("comptabilite.accountingClassNotAssigned") }}
          </span>
          <a-button size="small" type="link" @click="assignClass(record)">
            {{ $t("comptabilite.accountingClassAssign") }}
          </a-button>
        </span>
        <span v-else class="text-muted small">
          {{ $t("comptabilite.accountingClassParentMissing") }}
        </span>
      </template>
      <template slot="action" slot-scope="text, record">
        <div class="editable-row-operations">
          <a-button type="primary" size="small" @click="openModal(record)">
            {{ $t("action.modifier") }}
          </a-button>
          <a-button type="danger" size="small" class="ml-2">
            <a-popconfirm
              :title="$t('requis.supp')"
              @confirm="() => remove(record._id)"
            >
              <a>{{ $t("action.supprimer") }}</a>
            </a-popconfirm>
          </a-button>
        </div>
      </template>
    </a-table>

    <a-modal
      :visible="modalVisible"
      :title="editingId ? $t('action.modifier') : $t('action.ajouter')"
      :confirm-loading="saving"
      :width="640"
      @ok="onModalOk"
      @cancel="closeModal"
      destroy-on-close
      wrap-class-name="parametrage-compact-modal"
    >
      <a-form :form="form" layout="vertical" class="compact-param-form">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('etablissement.nom')">
              <a-input
                v-decorator="[
                  'name',
                  { rules: [{ required: true, message: $t('requis.nom') }] },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.category')">
              <a-select
                v-decorator="[
                  'category',
                  {
                    rules: [{ required: true, message: $t('requis.depense') }],
                  },
                ]"
                show-search
                option-filter-prop="children"
              >
                <a-select-option
                  v-for="c in categories"
                  :key="c._id"
                  :value="c._id"
                >
                  {{ c.name }} ({{ c.depenseType && c.depenseType.name }})
                  <span v-if="c.accountingClass" class="text-muted">
                    — {{ c.accountingClass }}
                  </span>
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.direction')">
              <a-select
                v-decorator="[
                  'direction',
                  { initialValue: 'normal', rules: [{ required: true }] },
                ]"
              >
                <a-select-option value="normal">{{
                  $t("comptabilite.directionNormal")
                }}</a-select-option>
                <a-select-option value="inverse">{{
                  $t("comptabilite.directionInverse")
                }}</a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingClass')">
              <a-input
                :value="
                  editingId && editingClass
                    ? editingClass
                    : $t('comptabilite.accountingClassAutoGenerated')
                "
                disabled
              />
              <div class="text-muted small">
                {{ $t("comptabilite.accountingClassAutoGeneratedHint") }}
              </div>
            </a-form-item>
          </div>
        </div>
        <a-form-item :label="$t('comptabilite.description')">
          <a-textarea
            v-decorator="['description', { initialValue: '' }]"
            :rows="3"
            :placeholder="$t('comptabilite.description')"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "SubCategoriesTab",
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      loading: false,
      rows: [],
      categories: [],
      modalVisible: false,
      saving: false,
      editingId: null,
      editingClass: "",
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("etablissement.nom"),
          dataIndex: "name",
          key: "name",
        },
        {
          title: this.$t("comptabilite.category"),
          key: "cat",
          scopedSlots: { customRender: "cat" },
        },
        {
          title: this.$t("comptabilite.direction"),
          dataIndex: "direction",
          key: "direction",
          scopedSlots: { customRender: "dir" },
        },
        {
          title: this.$t("comptabilite.accountingClass"),
          dataIndex: "accountingClass",
          key: "accountingClass",
          scopedSlots: { customRender: "accountingClass" },
        },
        {
          title: this.$t("comptabilite.description"),
          dataIndex: "description",
          key: "description",
          ellipsis: true,
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          fixed: "right",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  created() {
    this.loadCategories();
    this.load();
  },
  methods: {
    parentCategoryClass(record) {
      if (!record) return "";
      const cat = record.category;
      if (cat && typeof cat === "object" && cat.accountingClass) {
        return cat.accountingClass;
      }
      const cid = cat && cat._id ? cat._id : cat;
      const found = (this.categories || []).find(
        (c) => String(c._id) === String(cid)
      );
      return (found && found.accountingClass) || "";
    },
    async loadCategories() {
      try {
        const res = await apiClient.get("/compV3/depense/categories");
        this.categories = res.data || [];
      } catch (e) {
        this.categories = [];
      }
    },
    async load() {
      this.loading = true;
      try {
        const res = await apiClient.get("/compV3/depense/subcategories");
        this.rows = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    openModal(record) {
      this.editingId = record ? record._id : null;
      this.editingClass = (record && record.accountingClass) || "";
      this.modalVisible = true;
      this.$nextTick(() => {
        if (record) {
          const catId =
            record.category && record.category._id
              ? record.category._id
              : record.category;
          this.form.setFieldsValue({
            name: record.name,
            category: catId,
            direction: record.direction || "normal",
            description: record.description || "",
          });
        } else {
          this.form.resetFields();
          this.form.setFieldsValue({
            direction: "normal",
            description: "",
          });
        }
      });
    },
    closeModal() {
      this.modalVisible = false;
      this.editingId = null;
      this.editingClass = "";
    },
    onModalOk(e) {
      e.preventDefault();
      this.submit();
    },
    submit() {
      this.form.validateFields((err, values) => {
        if (err) return;
        this.saving = true;
        const payload = { data: values };
        const req = this.editingId
          ? apiClient.patch(
              `/compV3/depense/subcategories/${this.editingId}`,
              payload
            )
          : apiClient.put("/compV3/depense/subcategories", payload);
        req
          .then(() => {
            this.$message.success(this.$t("success.operationEffectue"));
            this.closeModal();
            this.load();
          })
          .catch((err) => showApiError(this, err))
          .finally(() => {
            this.saving = false;
          });
      });
    },
    assignClass(record) {
      apiClient
        .patch(
          `/compV3/depense/subcategories/${record._id}/assign-class`,
          { data: {} }
        )
        .then(() => {
          this.$message.success(this.$t("success.operationEffectue"));
          this.load();
        })
        .catch((err) => showApiError(this, err));
    },
    remove(id) {
      apiClient
        .patch(`/compV3/depense/subcategories/${id}`, {
          data: { status: "inactive" },
        })
        .then(() => {
          this.$message.success(this.$t("success.operationEffectue"));
          this.load();
          this.loadCategories();
        })
        .catch((err) => showApiError(this, err));
    },
  },
};
</script>
<style scoped>
.compact-param-form >>> .ant-form-item {
  margin-bottom: 10px;
}
</style>
